import { useState, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import globals from 'styles/globals';

import convertByteString, { GB } from 'lib/utils/string/convertByteString';
import getLicense from 'lib/license';
import { useModal } from 'hooks/useModal';
import { useUnwrappedSelector } from 'hooks/useUnwrappedSelector';

import UnusedLicensesModal from 'containers/Modals/UnusedLicensesModal';
import { useOrganizationUsersOptions } from 'hooks/useOrganizationUsersOptions';
import { useLicenses } from 'components/CreateOrgModal/useLicenses';
import SelectLicenseTable from 'components/CreateOrgModal/SelectLicenseTable';
import Combobox from 'components/Combobox/index';
import { surrenderOrganization, getCurrentOrganization, getIsOrgOwner, getOrganizationScopes } from 'rdx/modules/organization/slice';
import { getOrgUsersRaw, getUser } from 'rdx/modules/users/slice';
import OrganizationStorageDetails from './OrganizationStorageDetails';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { LicenseAttributes } from 'types/licenses';
import Resource from 'lib/jsonApi/Resource';
import { UserAttributes } from 'types/user';
import { OrganizationUserAttributes } from 'types/organizationUsers';
import DangerButton from 'components/Button/DangerButton';
import PrimaryButton from 'components/Button/PrimaryButton';

const { fonts, colors } = globals;

type LicenseTabProps = {
  storageView: boolean,
  setStorageView: React.Dispatch<React.SetStateAction<boolean>>
  licenseView: boolean,
  setLicenseView: React.Dispatch<React.SetStateAction<boolean>>
}

const LicenseTab = ({ storageView, setStorageView, licenseView, setLicenseView }: LicenseTabProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { setModal, closeModal, callModal } = useModal();

  const organization = useAppSelector(getCurrentOrganization);
  const updateAction = useMemo(() => organization?.getAction('update') ?? null, [organization]);
  const viewOnly = !updateAction;
  const user = useAppSelector(getUser);
  const users = useUnwrappedSelector<OrganizationUserAttributes>(getOrgUsersRaw);
  const licenses = useLicenses();

  const [surrenderOrg, setSurrenderOrg] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<number | string>('');

  const isOrgOwner = useAppSelector(getIsOrgOwner);
  const currentUserOrgScopes = useAppSelector(getOrganizationScopes);

  const [
    unusedLicenses,
    attachAction,
    surrenderAction,
    orgLicenses,
    orgMostExpensiveLicense,
    totalConfirmedUsers,
    totalPendingUsers,
    storageLimit,
    seatsLimit,
    activeWorkflows,
  ] = useMemo(() => [
    organization?.getRel('unused_licenses'),
    organization?.getAction('attach_license'),
    organization?.getAction('change_owner'),
    organization?.getRel<LicenseAttributes>('licenses'),
    getLicense(organization?.getRel<LicenseAttributes>('licenses') as Resource<LicenseAttributes>[]),
    organization?.attributes?.total_confirmed_users,
    organization?.attributes?.total_pending_users,
    organization?.attributes?.storage_limit,
    organization?.attributes?.seats_limit,
    organization?.attributes?.active_workflows,
  ], [organization]);

  const isTrial = orgMostExpensiveLicense?.attributes?.pn?.includes('TRIAL');
  const expiresAt = orgMostExpensiveLicense?.attributes?.expires_at;
  const startedOn = moment(orgMostExpensiveLicense?.attributes?.created_at).format('LLL');
  const expiresIn = moment(expiresAt).fromNow();

  const {
    userDropdownOptions,
    setOwnerSearch,
  } = useOrganizationUsersOptions({});

  const workflows = useMemo(() => {
    if (Array.isArray(orgLicenses)) {
      const maxWorkflows = Math.max(...orgLicenses.map((license) => license.attributes?.meta.max_workflows ?? 0));
      return maxWorkflows;
    } else {
      return orgMostExpensiveLicense?.attributes?.meta?.max_workflows ?? 0;
    }
  }, [orgLicenses, orgMostExpensiveLicense?.attributes?.meta?.max_workflows]);

  // show storage details
  if (storageView) {
    return (
      <OrganizationStorageDetails
        licenseLimit={storageLimit * GB}
        onCancel={() => setStorageView(false)}
      />
    );
  }

  if (confirm) {
    const orgUserToTransferTo = Array.isArray(users) ? users.find((u) => u.attributes.user_id === selectedUser) : null;
    const transferTo = orgUserToTransferTo ? orgUserToTransferTo.getRel<UserAttributes>('user') : null;
    let fullName = '';
    let confirmMsg = '';
    if (transferTo instanceof Resource) {
      fullName = `${transferTo?.attributes?.first_name} ${transferTo?.attributes?.last_name} - ${transferTo?.attributes?.email}`;
      confirmMsg = `Are you sure you wish to make ${fullName} the administrator for ${organization.attributes.name}?  This action is irreversible.`;
    }

    const transferOwnership = () => {
      dispatch(surrenderOrganization({
        action: surrenderAction,
        params: { new_owner_id: selectedUser },
        onSuccess: (payload) => {
          setModal(null);
          closeModal();
          if (payload?.routeTo) {
            history.push(payload.routeTo);
          }
        },
      }));
    };

    return (
      <VerticalFlexContainer>
        <ConfirmTitle>Warning:</ConfirmTitle>
        <ConfirmMessage>{confirmMsg}</ConfirmMessage>
        <ButtonContainer>
          <DangerButton
            data-testid="transfer-confirm"
            text="confirm"
            onClick={() => transferOwnership()}
          />
          <PrimaryButton
            hollow
            text="cancel"
            onClick={() => setConfirm(false)}
          />
        </ButtonContainer>
      </VerticalFlexContainer>
    );
  }

  if (surrenderOrg) {
    return (
      <VerticalFlexContainer>
        <Title>Change Organization Administrator</Title>
        <Subtitle>
          Groups owned by the current administrator will also be transferred to the new administrator.
        </Subtitle>
        <HorizontalContainerWithMargin>
          <Combobox
            width="300px"
            placeholder="Select another organization user"
            options={userDropdownOptions}
            // value={selectedUser}
            onChange={(value) => {
              setSelectedUser(value ?? '');
            }}
            onInputChange={(value) => setOwnerSearch(value ?? '')}
          />
        </HorizontalContainerWithMargin>
        <ButtonContainer>
          <DangerButton
            data-testid="transfer-submit"
            text="transfer"
            onClick={() => setConfirm(true)}
            disabled={!selectedUser}
          />
          <PrimaryButton
            hollow
            data-testid="transfer-cancel"
            text="cancel"
            onClick={() => setSurrenderOrg(false)}
            style={{ marginLeft: '10px' }}
          />
        </ButtonContainer>
      </VerticalFlexContainer>
    );
  }

  if (licenseView) {
    return (
      <VerticalFlexContainer>
        <SelectLicenseTable
          currentLicenses={orgLicenses as Resource<LicenseAttributes>[]}
          currentBestLicense={orgMostExpensiveLicense}
          onCancel={() => setLicenseView(false)}
        />
      </VerticalFlexContainer>
    );
  }

  // no license yet (new organization)
  if (!orgMostExpensiveLicense) {
    return (
      <VerticalFlexContainer>
        <Title>License Information</Title>
        <HorizontalContainerWithMargin>
          <Left>
            <Label>TYPE</Label>
            <Values>NONE</Values>
          </Left>
          <Right>
            <PrimaryButton
              hollow
              onClick={() => setLicenseView(true)}
              text="purchase"
              disabled={!licenses}
            />
          </Right>
        </HorizontalContainerWithMargin>
      </VerticalFlexContainer>
    );
  }

  const diskUsage = organization?.attributes?.total_size ? convertByteString(organization.attributes.total_size) : 'unavailable';

  // license details render
  return (
    <VerticalFlexContainer>
      <Title>License Information</Title>
      <HorizontalContainerWithMargin>
        <Left>
          {Array.isArray(orgLicenses) && (
            <>
              <Label>{orgLicenses.length > 1 ? 'TYPES' : 'TYPE'}</Label>
              {orgLicenses.map((license) => {
                return (
                  <Values key={license.id}>{`${license.attributes?.name}`}</Values>
                )
              })}
            </>
          )}
        </Left>
        {!viewOnly && (
          <Right>
            <PrimaryButton
              hollow
              data-testid="upgrade-button"
              onClick={() => setLicenseView(true)}
              text="upgrade"
            />
          </Right>
        )}
      </HorizontalContainerWithMargin>
      {isTrial && (
        <HorizontalContainerWithMargin>
          <Left>
            <Label>TRIAL STARTED ON</Label>
            <Values>{startedOn} (Expires {expiresIn})</Values>
            {totalPendingUsers > 0 && (
              <SmallValues>{`${totalPendingUsers} out of ${totalConfirmedUsers} are pending`}</SmallValues>
            )}
          </Left>
        </HorizontalContainerWithMargin>
      )}
      {!!seatsLimit && (
        <HorizontalContainerWithMargin>
          <Left>
            <Label>OCCUPIED SEATS</Label>
            <Values>{`${totalConfirmedUsers} out of ${seatsLimit} Occupied`}</Values>
            {totalPendingUsers > 0 && (
              <SmallValues>{`${totalPendingUsers} out of ${totalConfirmedUsers} are pending`}</SmallValues>
            )}
          </Left>
        </HorizontalContainerWithMargin>
      )}
      {!!workflows && (
        <HorizontalContainerWithMargin>
          <Left>
            <Label>ACTIVE WORKFLOWS</Label>
            <Values>{`${activeWorkflows} out of ${workflows === 1000000 ? 'Unlimited' : workflows}`}</Values>
          </Left>
        </HorizontalContainerWithMargin>
      )}
      {!!storageLimit && (
        <HorizontalContainerWithMargin>
          <Left>
            <Label>USED STORAGE</Label>
            <Values>{`${diskUsage} out of ${storageLimit} GB`}</Values>
          </Left>
          {(currentUserOrgScopes?.includes('files') || isOrgOwner) && (
            <Right>
              <PrimaryButton
                hollow
                onClick={() => { setStorageView(true); }}
                text="details"
              />
            </Right>
          )}
        </HorizontalContainerWithMargin>
      )}
      {surrenderAction && (
        <HorizontalContainerWithMargin>
          <Left>
            <Label>ORGANIZATION ADMIN</Label>
            <Values>{user.attributes.email}</Values>
          </Left>
          {!viewOnly && (
            <Right>
              <PrimaryButton
                hollow
                data-testid="transfer-button"
                onClick={() => setSurrenderOrg(true)}
                text="transfer"
              />
            </Right>
          )}
        </HorizontalContainerWithMargin>
      )}
      {Array.isArray(unusedLicenses) && !!unusedLicenses.length && attachAction && (
        <HorizontalContainerWithMargin>
          <Left>
            <Label>UNAPPLIED LICENSES</Label>
            <Values>You have{unusedLicenses.length > 1 ? ` ${unusedLicenses.length} unapplied licenses.` : ' an unapplied license.'}</Values>
          </Left>
          <Right>
            <PrimaryButton
              hollow
              data-testid="transfer-button"
              onClick={() => callModal(<UnusedLicensesModal key={uuid()} />)}
              text="Apply"
            />
          </Right>
        </HorizontalContainerWithMargin>
      )}
    </VerticalFlexContainer>
  );
};

export default LicenseTab;

const VerticalFlexContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 40px;
`;

const HorizontalContainerWithMargin = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  width: 100%;
  margin: 10px 0;
`;

const Title = styled.h1`
  flex: 1;
`;

const Subtitle = styled.p`
  flex: 1;
  margin: 10px 0;
  font-weight: 300;
`;

const ConfirmTitle = styled(Title)`
  color: ${({ theme }) => theme.colors.errorText};
`;

const ConfirmMessage = styled.p`
  color: ${({ theme }) => theme.colors.errorText};
`;

const Left = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-around;
`;

const Right = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
`;

const Label = styled.p<{ highlighted?: boolean }>`
  font: ${fonts.flag};
  padding: 5px 0;
  font-weight: bolder;
  color: ${({ highlighted }) => (highlighted ? colors.red : colors.fontDark)};
`;

const Values = styled.p<{ highlighted?: boolean }>`
  font-weight: 300;
  color: ${({ highlighted }) => (highlighted ? colors.red : colors.fontMed)};
`;
const SmallValues = styled.p<{ highlighted?: boolean }>`
  font-weight: 300;
  color: ${({ highlighted }) => (highlighted ? colors.red : colors.fontMed)};
  font-size: 11px;
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin-top: 190px;
  display: flex;
  justify-content: flex-end;
`;
